'use client';

import { ArrowLeft, KeyRound, Database, Lock, ShieldCheck, Server, Users, Bug } from 'lucide-react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { organizationSchema, breadcrumbSchema } from '@/lib/utils/structured-data';
import { SITE_URL } from '@/lib/site';

const measures = [
  {
    icon: KeyRound,
    title: 'Hashed API keys',
    body: 'API keys are stored as SHA-256 hashes, never in plaintext. The full key is shown only once at creation time.',
  },
  {
    icon: Database,
    title: 'Row-level security',
    body: 'Row-level security is enforced on all tenant data, so each customer can access only their own records.',
  },
  {
    icon: Lock,
    title: 'TLS in transit',
    body: 'All traffic between the SDK, the API, and the dashboard is encrypted in transit with TLS.',
  },
  {
    icon: ShieldCheck,
    title: 'PII sanitization in the SDK',
    body: 'Personal data is redacted client-side inside the SDK before any telemetry leaves your servers.',
  },
  {
    icon: Server,
    title: 'EU hosting',
    body: 'Data is stored and processed on secure, EU-hosted cloud infrastructure.',
  },
  {
    icon: Users,
    title: 'Least-privilege access',
    body: 'Internal access to systems and data follows least-privilege principles and is limited to a need-to-know basis.',
  },
];

const securityBreadcrumb = breadcrumbSchema([
  { name: 'Home', url: SITE_URL },
  { name: 'Security', url: `${SITE_URL}/security` },
]);

export default function SecurityPage() {
  return (
    <div className="min-h-screen bg-black">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(securityBreadcrumb) }} />
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-rose-500/5 rounded-full blur-3xl"></div>
      </div>

      <Header />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors group mb-8 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back</span>
        </Link>

        <div className="mb-12">
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-6">
            <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700"></div>
            Security
            <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700"></div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            <span className="bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent">
              How we protect your data
            </span>
          </h1>
          <p className="text-base text-neutral-400 max-w-2xl">
            Security is the product. Here is how Silker AI keeps your data safe across the SDK, the API, and the dashboard.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {measures.map(({ icon: Icon, title, body }) => (
            <section
              key={title}
              className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-rose-400" />
                </div>
                <h2 className="text-lg font-semibold text-white">{title}</h2>
              </div>
              <p className="text-sm text-neutral-400 leading-relaxed">{body}</p>
            </section>
          ))}
        </div>

        <section className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center">
              <Bug className="w-5 h-5 text-rose-400" />
            </div>
            <h2 className="text-2xl font-semibold text-white">Responsible disclosure</h2>
          </div>
          <p className="text-sm text-neutral-400 leading-relaxed">
            Found a vulnerability? We appreciate responsible disclosure. Please report security issues to{' '}
            <a href="mailto:security@silkerai.com" className="text-rose-400 hover:text-rose-300 transition">
              security@silkerai.com
            </a>{' '}
            and give us a reasonable window to investigate and remediate before any public disclosure.
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
